import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Stack, CircularProgress } from '@mui/material';
import { Lan, Tune, Settings, People, TabletAndroid } from '@mui/icons-material';

import { GatewaySettings } from './CompanyManager/GatewaySettings';
import { HardwareRegistry } from './CompanyManager/HardwareRegistry';
import { TabletConfig } from './CompanyManager/TabletConfig';
import { PersonnelSync } from './CompanyManager/PersonnelSync';
import { DeviceRegistry } from './CompanyManager/DeviceRegistry';

export const FleetConsole = ({ selectedOrg }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [configs, setConfigs] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const targetId = selectedOrg.cid || selectedOrg.id; 
      const response = await fetch(`https://console.dreamtec.io/customers/${targetId}/configs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch configs');
      const data = await response.json();
      const configMap = {};
      data.forEach(c => { configMap[c.environment] = c; });
      setConfigs(configMap);
    } catch (err) {
      console.error("Config fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg.id, selectedOrg.cid]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const logoUrl = selectedOrg.logo 
    ? `https://live.imeterbox.com/im_live/main/source/logos/${selectedOrg.logo}` 
    : null;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress sx={{ color: '#2F727C' }} />
    </Box>
  );

  const tabs = [
    { label: 'Gateways', icon: <Lan fontSize="small" />, component: <GatewaySettings organization={selectedOrg} initialConfigs={configs} onRefresh={fetchConfigs} /> },
    { label: 'Devices', icon: <TabletAndroid fontSize="small" />, component: <DeviceRegistry organization={selectedOrg} /> },
    { label: 'Hardware', icon: <Tune fontSize="small" />, component: <HardwareRegistry organization={selectedOrg} /> },
    { label: 'Config', icon: <Settings fontSize="small" />, component: <TabletConfig organization={selectedOrg} /> },
    { label: 'Personnel', icon: <People fontSize="small" />, component: <PersonnelSync organization={selectedOrg} /> },
  ];

  return (
    <Box 
      sx={{ 
        width: '100%', minHeight: '100vh',
        backgroundColor: '#f8fafc',
        p: 1.5 // Reduced from 4
      }}
    >
      {/* ─── HEADER (Compact) ─── */}
<Box sx={{ mb: 2 }}>
  <Typography
    variant="h6"
    sx={{
      fontWeight: 800,
      color: '#1A3A3F',
      mb: 1.5,
      letterSpacing: -0.5,
    }}
  >
    Fleet Management
  </Typography>

  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1.5,
      px: 1.2,
      py: 1,
      bgcolor: '#ffffff',
      border: '1px solid rgba(47,114,124,0.18)',
      borderRadius: '10px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}
  >
    {logoUrl && (
      <Box
        sx={{
          width: 140,
          height: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f4f8fb',
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <img
          src={logoUrl}
          alt="Logo"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>
    )}

    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.95rem',
          color: '#1A3A3F',
          lineHeight: 1.3,
        }}
      >
        {selectedOrg.name}
      </Typography>

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          mt: 0.6,
          px: 0.8,
          py: 0.15,
          bgcolor: '#1A3A3F',
          color: '#ffffff',
          borderRadius: '5px',
          fontSize: '0.62rem',
          fontWeight: 700,
          lineHeight: 1.5,
          letterSpacing: '0.2px',
        }}
      >
        CID: {selectedOrg.cid || selectedOrg.id}
      </Box>
    </Box>
  </Box>
</Box>
      {/* ─── WORKSPACE (Compact) ─── */}
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)', bgcolor: '#fff' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{ 
            borderBottom: 1, borderColor: 'divider', px: 1, bgcolor: '#ffffff',
            minHeight: 48, // Reduced from 64
            '& .MuiTab-root': { 
              fontWeight: 800, textTransform: 'none', minHeight: 48, gap: 1, fontSize: '0.8rem', padding: '6px 16px' 
            },
            '& .Mui-selected': { color: '#2F727C !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#2F727C', height: 3 }
          }}
        >
          {tabs.map((t, i) => <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />)}
        </Tabs>
        
        {/* REDUCED INTERNAL PADDING */}
        <Box sx={{ p: 2 }}> 
          {tabs[activeTab].component}
        </Box>
      </Paper>
    </Box>
  );
};